"use client";

import type { ProtocolMetadata, SupSingleVaultV3 } from "@/types";
import React, { useCallback, useMemo, useState } from "react";

import LayoutSidebarFiltersAtomHeader from "@/components/Grid/Bar/Filters/Atom/Header";
import UiLabelPlaceholder from "@/components/Ui/Label/Placeholder";

import { type Column, SupDataGrid, SupIconButton, SupInput, SupTracker, SupIconCheckbox, SupCheckboxChecked, SupSearch } from "@repo/core-ui";
import useProtocols from "@/hooks/api/useProtocols";
import { useStoredData, useStoredDataDispatch } from "@/store/Context";
import { formatCurrencyCompact, getProtocolImage, getProtocolName, isEmptyAmount, mapProtocolIcon } from "@/utils";

interface singleProtocol {
  image_url: string;
  protocol: string;
  metadata?: ProtocolMetadata;
}

interface LayoutSidebarFiltersProtocolProps {
  vaults?: SupSingleVaultV3[];
}

export default function LayoutSidebarFiltersProtocol({ vaults }: LayoutSidebarFiltersProtocolProps) {
  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();

  const { data: protocols, isLoading: isLoadingProtocols, isError: isErrorProtocol } = useProtocols("fully-cached");

  const [query, setQuery] = useState<string>("");

  const selectedProtocols = useMemo(() => {
    const filterValues = storedData.filters.find(storedFilter => storedFilter.name === "protocol");
    return (filterValues ? filterValues.values : []) as string[];
  }, [storedData.filters]);

  const supportedProtocols = useMemo(() => {
    const protocolList = [] as singleProtocol[];
    vaults
      ?.filter(vault => vault.visibility !== "hidden")
      .forEach(vault => {
        if (!protocolList.find(item => item.protocol === getProtocolName(vault))) {
          protocolList.push({
            image_url: getProtocolImage(vault),
            protocol: getProtocolName(vault),
            metadata: protocols?.find(item => vault.protocol && item.id === vault.protocol_id),
          });
        }
      });
    if (query.length < 1) {
      return protocolList;
    }
    return protocolList.filter(item => {
      return `${item.protocol}`.toLowerCase().includes(query.toLowerCase());
    });
  }, [vaults, protocols, query]);

  const handleProtocolSelection = useCallback(
    (newProtocol: singleProtocol) => {
      const updatedProtocolList = selectedProtocols.includes(newProtocol.protocol)
        ? [...selectedProtocols.filter(protocol => protocol !== newProtocol.protocol)]
        : [...selectedProtocols, newProtocol.protocol];
      storedDispatch({ type: "setFilter", filter: { name: "protocol", values: updatedProtocolList } });
    },
    [selectedProtocols, storedDispatch]
  );

  const columns: Column<singleProtocol>[] = [
    {
      minWidth: 30,
      maxWidth: 30,
      field: "select",
      cellClassName: "cursor-pointer",
      headerName: "",
      sortable: false,
      draggable: false,
      resizable: false,
      valueGetter: row => `checkbox-${row.protocol}`,
      renderCell: row => (
        <SupIconButton active={selectedProtocols.includes(row.protocol)} activeIcon={<SupCheckboxChecked />}>
          <SupIconCheckbox />
        </SupIconButton>
      ),
    },
    {
      minWidth: 155,
      maxWidth: 155,
      draggable: false,
      resizable: false,
      field: "protocol",
      cellClassName: "cursor-pointer",
      headerName: "Protocol",
      valueGetter: row => `${row.protocol}`,
      renderCell: row => mapProtocolIcon(row, false, true),
    },
    {
      minWidth: 80,
      maxWidth: 80,
      draggable: false,
      resizable: false,
      field: "tvl",
      cellClassName: "cursor-pointer",
      headerName: "TVL",
      type: "number",
      align: "right",
      valueGetter: row => row.metadata?.tvl ?? -1,
      renderCell: row => {
        return !row.metadata || isEmptyAmount(row.metadata.tvl) ? (
          <UiLabelPlaceholder>N/A</UiLabelPlaceholder>
        ) : (
          <span className="text-xs">{formatCurrencyCompact(row.metadata.tvl)}</span>
        );
      },
    },
    {
      minWidth: 100,
      maxWidth: 100,
      field: "is_authenticated",
      cellClassName: "cursor-pointer",
      headerName: "Status",
      type: "boolean",
      valueGetter: row => row.metadata?.is_authenticated ?? false,
      renderCell: row => (
        <SupTracker className="px-1 text-[8px]" variant={row.metadata?.is_authenticated ? "success" : "primary"}>
          {row.metadata?.is_authenticated ? "Authenticated" : "Unauthenticated"}
        </SupTracker>
      ),
    },
  ];

  return (
    <LayoutSidebarFiltersAtomHeader name="protocol" isLoading={isLoadingProtocols} disabled={!supportedProtocols?.length || isLoadingProtocols || isErrorProtocol}>
      <div className="mt-[16px] space-y-[16px]">
        <SupInput clearable type="text" prefixIcon={<SupSearch />} id="balance-search" value={query} fluid placeholder="SEARCH NAME" onChange={e => setQuery(e ?? "")} />
        <SupDataGrid
          onRowClick={row => handleProtocolSelection(Array.isArray(row) ? row[0] : row)}
          className="h-full min-h-[240px]"
          loading={isLoadingProtocols}
          data={supportedProtocols}
          defaultSorting={[{ field: "tvl", direction: "DESC" }]}
          rowId={row => `protocol-filter-${row.protocol}`}
          columns={columns}
        />
      </div>
    </LayoutSidebarFiltersAtomHeader>
  );
}
